import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Paper,
  Alert,
  CircularProgress,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/auth';

const EmploymentLetterTemplate = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [formData, setFormData] = useState({
    institutionName: 'Liberia Motorcycle & Tricycle Security Service',
    institutionEmail: '',
    institutionPhone: '',
    institutionAddress: '',
    signByName: '',
    signByTitle: '',
    templateContent: '',
  });

  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [templateId, setTemplateId] = useState(null);

  // Available placeholders
  const placeholders = [
    { key: '{{employeeId}}', description: 'Employee ID (e.g., EMP001)' },
    { key: '{{firstName}}', description: 'Employee first name' },
    { key: '{{middleName}}', description: 'Employee middle name' },
    { key: '{{lastName}}', description: 'Employee last name' },
    { key: '{{fullName}}', description: 'Employee full name' },
    { key: '{{email}}', description: 'Employee email' },
    { key: '{{phoneNumber}}', description: 'Employee phone number' },
    { key: '{{jobTitle}}', description: 'Employee job title' },
    { key: '{{dateOfHire}}', description: 'Date employee was hired' },
    { key: '{{address}}', description: 'Employee address' },
    { key: '{{district}}', description: 'Employee district' },
    { key: '{{county}}', description: 'Employee county' },
    { key: '{{countryOfOrigin}}', description: 'Employee country of origin' },
    { key: '{{educationalBackground}}', description: 'Employee education' },
    { key: '{{maritalStatus}}', description: 'Employee marital status' },
    { key: '{{tor}}', description: 'Employee Terms of Reference' },
    { key: '{{currentDate}}', description: 'Current date (when letter is generated)' },
    { key: '{{institutionName}}', description: 'Institution name' },
    { key: '{{institutionEmail}}', description: 'Institution email' },
    { key: '{{institutionPhone}}', description: 'Institution phone' },
    { key: '{{institutionAddress}}', description: 'Institution address' },
    { key: '{{signByName}}', description: 'Name of person signing letter' },
    { key: '{{signByTitle}}', description: 'Title of person signing letter' },
  ];

  const defaultTemplate = `
<p>Date: {{currentDate}}</p>

<h3>TO WHOM IT MAY CONCERN</h3>

<p>This letter serves to certify that <strong>{{fullName}}</strong> (Employee ID: {{employeeId}}) has been employed with {{institutionName}} since {{dateOfHire}}.</p>

<p>{{firstName}} {{lastName}} currently holds the position of <strong>{{jobTitle}}</strong> within our organization.</p>

<h4>Terms of Reference:</h4>
<p>{{tor}}</p>

<p>{{firstName}} has demonstrated professionalism and dedication in their role. This letter is issued upon request for official purposes.</p>

<p>Should you require any additional information, please feel free to contact us at {{institutionEmail}} or {{institutionPhone}}.</p>

<p>Sincerely,</p>
  `.trim();

  useEffect(() => {
    fetchActiveTemplate();
  }, []);

  const fetchActiveTemplate = async () => {
    setFetchLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/api/employment-letters/templates/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const template = response.data.template;
      setTemplateId(template.id);
      setFormData({
        institutionName: template.institutionName || '',
        institutionEmail: template.institutionEmail || '',
        institutionPhone: template.institutionPhone || '',
        institutionAddress: template.institutionAddress || '',
        signByName: template.signByName || '',
        signByTitle: template.signByTitle || '',
        templateContent: template.templateContent || '',
      });

      if (template.logoPath) {
        setLogoPreview(`${baseUrl}${template.logoPath}`);
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      if (error.response?.status === 404) {
        // No template exists, use default
        setFormData(prev => ({ ...prev, templateContent: defaultTemplate }));
      } else {
        setError('Failed to load template');
      }
    } finally {
      setFetchLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      setLogo(file);
      setLogoPreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const insertPlaceholder = (placeholder) => {
    const textarea = document.getElementById('templateContent');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.templateContent;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);

    setFormData(prev => ({
      ...prev,
      templateContent: before + placeholder + after
    }));

    // Set cursor position after inserted placeholder
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + placeholder.length;
      textarea.focus();
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate required fields
      if (!formData.institutionName || !formData.templateContent) {
        setError('Institution name and template content are required');
        setLoading(false);
        return;
      }

      // Create FormData for multipart/form-data
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          data.append(key, formData[key]);
        }
      });

      if (logo) {
        data.append('logo', logo);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      };

      if (templateId) {
        await axios.put(`${baseUrl}/api/employment-letters/templates/${templateId}`, data, config);
        setSuccess('Template updated successfully!');
      } else {
        const response = await axios.post(`${baseUrl}/api/employment-letters/templates`, data, config);
        setTemplateId(response.data.template.id);
        setSuccess('Template created successfully!');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setError(error.response?.data?.error || 'Failed to save template. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard/employees')}
          variant="outlined"
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          Employment Letter Template
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Placeholders Help */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            <Typography variant="h6">Available Placeholders</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Click on any placeholder below to insert it into your template at the cursor position.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {placeholders.map((item) => (
              <Chip
                key={item.key}
                label={item.key}
                onClick={() => insertPlaceholder(item.key)}
                color="primary"
                variant="outlined"
                title={item.description}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Paper elevation={3} sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          {/* Institution Logo */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Institution Logo
            </Typography>
            {logoPreview && (
              <Box sx={{ mb: 2 }}>
                <img
                  src={logoPreview}
                  alt="Institution Logo"
                  style={{ maxWidth: '200px', maxHeight: '100px' }}
                />
              </Box>
            )}
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              Upload Logo
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileChange}
              />
            </Button>
          </Box>

          {/* Institution Information */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            Institution Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Institution Name"
                name="institutionName"
                value={formData.institutionName}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="institutionEmail"
                type="email"
                value={formData.institutionEmail}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="institutionPhone"
                value={formData.institutionPhone}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="institutionAddress"
                value={formData.institutionAddress}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>

          {/* Signature Information */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            Signature Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Signed By (Name)"
                name="signByName"
                value={formData.signByName}
                onChange={handleInputChange}
                placeholder="e.g., John Doe"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Signed By (Title)"
                name="signByTitle"
                value={formData.signByTitle}
                onChange={handleInputChange}
                placeholder="e.g., Director of Human Resources"
              />
            </Grid>
          </Grid>

          {/* Template Content */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            Letter Template
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            You can use HTML tags for formatting (e.g., &lt;p&gt;, &lt;strong&gt;, &lt;h3&gt;).
            Click on placeholders above to insert them at the cursor position.
          </Alert>
          <TextField
            id="templateContent"
            fullWidth
            required
            multiline
            rows={20}
            name="templateContent"
            value={formData.templateContent}
            onChange={handleInputChange}
            placeholder="Enter the employment letter template here..."
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.9rem',
              }
            }}
          />

          {/* Submit Button */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard/employees')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Save Template'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default EmploymentLetterTemplate;
